import * as z from 'zod';
import API from './API.ts';
import { queryOptions } from '@tanstack/vue-query';
import { ReservableProductCategoryResource } from '@/types/reservations/ReservableProductCategory';
import { ReservableProductReservationOptionUpdateInput, ReservableProductResource } from '@/types/reservations/ReservableProduct';
import { ReservationItemResource } from '@/types/reservations/ReservationItem';
import { ReservationSentEmail } from '@/types/reservations/ReservationSentEmail';
import { ReservableResource } from '@/types/reservations/Reservable';
import { LockData } from '@/types/reservations/Lock';
import { UpcomingCheckModes } from '@/types/reservations/ReservableProductCheckIn';
import { FollowingReservation } from '@/types/reservations/FollowingReservation';
import { EditingRecordTimeline } from '@/types/EditingRecordTimeline';
import { CheckInItemResult } from '@/types/reservations/CheckInItemResult';
import { SendBookingAddonsEmailResult } from '@/types/reservations/SendBookingAddonsEmailResult';
import { SalesChannelAssortmentProduct } from '@/types/reservations/SalesChannelAssortment';
import { ReservableProductWithDetailsResource } from '@/types/reservations/ReservableProductWithDetails';
import { ReservableProductLiveUsageResource } from '@/types/reservations/ReservableProductLiveUsage';
import { CreateReservationSuccessResponse, EnrichedReservation, ReservationModel } from '@/types/reservations/Reservation';
import { AvailabilityCheckResult } from '@/types/reservations/AvailabilityCheckResult';
import { ProductMatrixResource } from '@/types/reservations/BookingMatrix';
import { ReservationOverviewResource } from '@/types/reservations/ReservationOverview';
import { EntityId } from '@/types/base-schemas.ts';
import { isoDateTimeToDayJs } from '@/types/base-codecs.ts';
import { UpdatePoolModeStockQuantitySuccessResponse } from '@/types/reservations/UpdatePoolModeStockQuantity';
import { AdminValidateCartItemInput, ValidateCartResponse } from '@/types/reservations/ValidateCart';
import { AddonsEmailSettings } from '@/types/reservations/AddonsEmailSettings';
const checkInItem = (itemId, deliveredAt = null) => API.httpClient.post(`reservations/reservation-items/${itemId}/mark-delivered`, {
    delivered_at: deliveredAt?.toISOString()
})
    .then(API.assertSuccessResponse)
    .then(CheckInItemResult.parse);
const fetchReservationItems = (options) => API.httpClient.get('reservations/reservation-items', {
    params: {
        offset: options.offset,
        limit: options.limit,
        sort_by: options.sortBy,
        sort_desc: options.sortDesc?.map(desc => desc ? 1 : 0),
        active_only: options.activeOnly ? 1 : 0,
        commitment_states: options.commitmentStates,
        view: options.view,
        product_ids: options.productIds,
        sources: options.sources,
        status: options.statuses,
        payment_status: options.paymentStatuses,
        period_start: options.periodStart?.toISOString(),
        period_end: options.periodEnd?.toISOString()
    }
})
    .then(API.assertSuccessResponse)
    .then(z.object({
    reservation_items: ReservationItemResource.array(),
    total_count: z.int()
}).parse);
export default {
    /**
     * List all reservable product categories (including empty ones) — for admin dropdowns
     */
    listAllReservableProductCategories: queryOptions({
        queryKey: ['reservations', 'reservableProductCategories', 'all'],
        queryFn: () => API.httpClient.get('reservations')
            .then(API.assertSuccessResponse)
            .then(z.object({
            categories: z.array(ReservableProductCategoryResource)
        }).transform(data => data.categories).parse)
    }),
    /**
     * List all reservable products with categories
     */
    listReservableProducts: () => queryOptions({
        queryKey: ['reservations', 'reservableProducts', 'list'],
        queryFn: () => API.httpClient.get('reservations/reservable-products')
            .then(API.assertSuccessResponse)
            .then(z.object({
            categories: z.array(ReservableProductCategoryResource)
        }).transform(data => data.categories).parse)
    }),
    /**
     * Get comprehensive details about a single reservable product
     */
    /** Reservationer som behöver samma lagerartiklar strax efter att reservationen slutar. */
    followingReservations: (itemId) => queryOptions({
        queryKey: ['reservations', 'followingReservations', itemId],
        queryFn: () => API.httpClient.get(`reservations/reservation-items/${itemId}/following-reservations`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            following_reservations: z.array(FollowingReservation)
        }).transform(data => data.following_reservations).parse)
    }),
    /** Who changed the product's settings, booking options, prices, items and confirmation text. */
    reservableProductEditingRecords: (productId) => queryOptions({
        queryKey: ['reservations', 'reservableProduct', 'editingRecords', productId],
        queryFn: () => API.httpClient.get(`reservations/reservable-products/${productId}/editing-records`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            editing_records: EditingRecordTimeline
        }).transform(data => data.editing_records).parse)
    }),
    /** Who checked the item in and out, what was undone, and changed times. */
    /** The history of every reservation in the reservation order, each entry saying which reservation it is about. */
    reservationEditingRecords: (reservationId) => queryOptions({
        queryKey: ['reservations', 'reservationEditingRecords', reservationId],
        queryFn: () => API.httpClient.get(`reservations/reservations/${reservationId}/editing-records`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            editing_records: EditingRecordTimeline
        }).transform(data => data.editing_records).parse)
    }),
    itemEditingRecords: (itemId) => queryOptions({
        queryKey: ['reservations', 'itemEditingRecords', itemId],
        queryFn: () => API.httpClient.get(`reservations/reservation-items/${itemId}/editing-records`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            editing_records: EditingRecordTimeline
        }).transform(data => data.editing_records).parse)
    }),
    upcomingReservationCheckModes: (productId) => queryOptions({
        queryKey: ['reservations', 'reservableProduct', 'upcomingCheckModes', productId],
        queryFn: () => API.httpClient.get(`reservations/reservable-products/${productId}/upcoming-reservations`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            upcoming_reservations: z.array(UpcomingCheckModes)
        }).transform(data => data.upcoming_reservations).parse)
    }),
    reservableProductDetails: (productId) => queryOptions({
        queryKey: ['reservations', 'reservableProduct', 'details', productId],
        queryFn: () => API.httpClient.get(`reservations/reservable-products/${productId}`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductWithDetailsResource
        }).transform(data => data.product).parse)
    }),
    /**
     * Hur mycket av produkten som är incheckat just nu, vad som är bokat men inte incheckat
     * och som mest incheckat samtidigt det senaste dygnet. Hämtas om varje minut.
     */
    reservableProductLiveUsage: (productId) => queryOptions({
        queryKey: ['reservations', 'reservableProduct', 'liveUsage', productId],
        queryFn: () => API.httpClient.get(`reservations/reservable-products/${productId}/live-usage`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            live_usage: ReservableProductLiveUsageResource
        }).transform(data => data.live_usage).parse),
        refetchInterval: 60000
    }),
    /**
     * Get daily operations (deliveries and returns) for a specific date.
     * Full-day products use half-open intervals [start, end) where the return date
     * at midnight indicates the item is due back the previous day.
     */
    dailyOperations: (options) => queryOptions({
        queryKey: ['reservations', 'dailyOperations', options?.date?.format('YYYY-MM-DD')],
        queryFn: () => API.httpClient.get('reservations/daily', {
            params: {
                date: options?.date?.format('YYYY-MM-DD')
            }
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            daily_operations: z.object({
                deliveries: ReservationItemResource.array(),
                returns: ReservationItemResource.array(),
                delivery_progress: z.object({
                    completed_count: z.number().int(),
                    total_count: z.number().int()
                }),
                return_progress: z.object({
                    completed_count: z.number().int(),
                    total_count: z.number().int()
                })
            })
        }).transform(data => data.daily_operations).parse)
    }),
    reservationOverview: (options) => queryOptions({
        queryKey: ['reservations', 'reservationOverview', options?.startAt?.toISOString(), options?.endAt?.toISOString(), options?.categoryId],
        queryFn: () => API.httpClient.get('reservations/reservation-overview', {
            params: {
                start_at: options?.startAt?.toISOString(),
                end_at: options?.endAt?.toISOString()
            }
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            reservation_overview: ReservationOverviewResource
        }).transform(data => data.reservation_overview).parse)
    }),
    bookingMatrixForProduct: (productId, options) => queryOptions({
        queryKey: ['reservations', 'bookingMatrix', 'product', productId, options?.startAt?.toISOString(), options?.endAt?.toISOString()],
        queryFn: () => API.httpClient.get(`reservations/reservable-products/${productId}/booking-matrix`, {
            params: {
                start_at: options?.startAt?.toISOString(),
                end_at: options?.endAt?.toISOString()
            }
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            booking_matrix: ProductMatrixResource
        }).transform(data => data.booking_matrix).parse)
    }),
    /**
     * Lås som kan kopplas till produkter och objekt: Igloo-lås och Tally-tjänster.
     */
    listLocks: queryOptions({
        queryKey: ['reservations', 'locks'],
        queryFn: () => API.httpClient.get('reservations/locks')
            .then(API.assertSuccessResponse)
            .then(z.object({
            locks: z.array(LockData)
        }).transform(data => data.locks).parse)
    }),
    //
    // Reservable Product Items Management (GET)
    //
    /**
     * List all reservable product items with pagination and sorting
     */
    listReservationItems: (options) => queryOptions({
        queryKey: ['reservations', 'reservationItems', 'list', options],
        queryFn: () => fetchReservationItems(options)
    }),
    /**
     * Antal reservationsrader i var och en av serverns vyer
     */
    countReservationItems: () => queryOptions({
        queryKey: ['reservations', 'reservationItems', 'counts'],
        queryFn: () => API.httpClient.get('reservations/reservation-items/counts')
            .then(API.assertSuccessResponse)
            .then(z.object({ counts: z.record(z.string(), z.int()) }).transform(data => data.counts).parse)
    }),
    //
    // Stock Management (GET)
    //
    /**
     * View detailed information about a single reservable (stock item)
     */
    getReservable: (reservableId) => queryOptions({
        queryKey: ['reservations', 'reservable', reservableId],
        queryFn: () => API.httpClient.get(`reservations/reservables/${reservableId}`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            reservable: ReservableResource
        }).transform(data => data.reservable).parse)
    }),
    /**
     * List all reservations with pagination and sorting
     */
    listReservations: (options) => queryOptions({
        queryKey: ['reservations', 'reservationsList', options],
        queryFn: () => API.httpClient.get('reservations/reservations', {
            params: {
                offset: options.offset,
                limit: options.limit,
                sort_by: options.sortBy,
                sort_desc: options.sortDesc ? 1 : 0,
                product_ids: options.productIds
            }
        })
            .then(API.assertSuccessResponse)
            .then(data => {
            const typedData = data;
            typedData.reservations.forEach(reservation => window.enrich.enrichReservation(reservation));
            return typedData;
        })
            .then(z.object({
            reservations: EnrichedReservation.array(),
            total_count: z.int()
        }).parse)
    }),
    /**
     * Get a single reservation by ID with all its items
     */
    getReservation: (reservationId) => queryOptions({
        queryKey: ['reservations', 'showReservation', reservationId],
        queryFn: () => API.httpClient.get('reservations/reservations/show', {
            params: { reservation_id: reservationId }
        })
            .then(API.assertSuccessResponse)
            .then(z.object({ reservation_resource: ReservationModel }).parse)
            .then(data => data.reservation_resource)
    }),
    //
    // Core Reservation Management (POST)
    //
    validateCart: (input) => queryOptions({
        queryKey: ['reservations', 'validateCart', input.bookingId ?? null, input.excludeItemId ?? null, input.cartItems],
        queryFn: () => API.httpClient.post('reservations/validate-cart', {
            booking_id: input.bookingId ?? null,
            cart_items: AdminValidateCartItemInput.array().encode(input.cartItems),
            exclude_item_id: input.excludeItemId ?? null
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            cart_validation_result: ValidateCartResponse
        }).transform(data => data.cart_validation_result).parse),
        enabled: input.cartItems.length > 0
    }),
    /**
     * Create a new reservation
     * Returns mutation options for useMutation
     */
    createReservation: {
        mutationKey: ['reservations', 'createReservation'],
        mutationFn: ({ customerId, bookingId = null, items, sendConfirmation = false }) => API.httpClient.post('reservations', {
            customer_id: customerId,
            booking_id: bookingId,
            items,
            send_confirmation: sendConfirmation
        })
            .then(API.assertSuccessResponse)
            .then(CreateReservationSuccessResponse.parse)
    },
    /**
     * Check stock availability for multiple products during a date/time period
     * Returns mutation options for useMutation
     */
    checkAvailability: {
        mutationKey: ['reservations', 'checkAvailability'],
        mutationFn: (items) => API.httpClient.post('reservations/check-availability', {
            items: items.map(item => ({
                reservable_product_id: item.reservable_product_id,
                quantity: item.quantity,
                start_datetime: item.start_datetime.toISOString(),
                end_datetime: item.end_datetime.toISOString()
            }))
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            availability_results: AvailabilityCheckResult
        }).transform(data => data.availability_results).parse)
    },
    //
    // Stock Management (POST)
    //
    /**
     * Add a new stock item to a product
     * Returns mutation options for useMutation
     */
    createReservable: {
        mutationKey: ['reservations', 'reservable', 'create'],
        mutationFn: ({ productId, identifier, isAvailable, notes, lock }) => API.httpClient.post(`reservations/reservable-products/${productId}/reservables`, {
            identifier,
            is_available: isAvailable,
            notes,
            lock
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            reservable: ReservableResource
        }).transform(data => data.reservable).parse)
    },
    //
    // Reservable Product Items Management (PUT)
    //
    /**
     * Update an existing reservation item.
     *
     * With `includeLockDependentFields: false` the quantity, units and schedule
     * are left out, so the server keeps the item's current values. The server
     * rejects those fields while the item has an active digital lock code, even
     * when they are unchanged.
     */
    updateItem: {
        mutationKey: ['reservations', 'reservationItem', 'update'],
        mutationFn: ({ reservationItemId, quantity, preferredReservableIds, deliveryDateTime, returnDateTime, price, priceMode, tierPrices, isManuallyScheduled, reservationOptionId, selection, note, skipSoftValidation = false, includeLockDependentFields = true }) => API.httpClient.put(`reservations/reservation-items/${reservationItemId}`, {
            ...(includeLockDependentFields
                ? {
                    quantity,
                    preferred_reservable_ids: preferredReservableIds ?? null,
                    delivery_date_time: isManuallyScheduled ? deliveryDateTime.toISOString() : undefined,
                    return_date_time: isManuallyScheduled ? returnDateTime.toISOString() : undefined,
                    is_manually_scheduled: isManuallyScheduled,
                    reservation_option_id: reservationOptionId,
                    selection
                }
                : {}),
            price,
            price_mode: priceMode,
            tier_prices: tierPrices,
            note: note || null,
            skip_soft_validation: skipSoftValidation
        })
            .then(API.assertSuccessResponse)
            .then(z.union([
            z.object({
                soft_validation_failure: z.literal(true),
                validation: ValidateCartResponse
            }),
            z.object({
                reservation_item: ReservationItemResource
            }).transform(data => data.reservation_item)
        ]).parse)
    },
    //
    // Stock Management (PUT)
    //
    /**
     * Update reservable details (name, availability, notes)
     * Cannot edit items currently in use
     * Returns mutation options for useMutation
     */
    updateReservable: {
        mutationKey: ['reservations', 'updateReservable'],
        mutationFn: ({ reservableId, identifier, isAvailable, notes, lock }) => API.httpClient.put(`reservations/reservables/${reservableId}`, {
            identifier,
            is_available: isAvailable,
            notes,
            lock
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            reservable: ReservableResource
        }).transform(data => data.reservable).parse)
    },
    //
    // Bookable Window Management (PATCH)
    //
    /**
     * Update bookable window for a product
     * Returns mutation options for useMutation
     */
    updateBookableWindow: {
        mutationKey: ['reservations', 'updateBookableWindow'],
        mutationFn: ({ productId, start, end }) => API.httpClient.patch(`reservations/reservable-products/${productId}/bookable-window`, { bookable_window: start && end ? { start, end } : null })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductWithDetailsResource
        }).transform(data => data.product).parse)
    },
    //
    // Admin Product Updates (PATCH)
    //
    updateProductName: {
        mutationKey: ['reservations', 'updateProductName'],
        mutationFn: ({ id, title, description }) => API.httpClient.patch(`reservations/reservable-products/${id}/name`, { title, description })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    updateProductReservationOptions: {
        mutationKey: ['reservations', 'updateProductReservationOptions'],
        mutationFn: ({ id, reservationOptions }) => API.httpClient.patch(`reservations/reservable-products/${id}/reservation-options`, {
            reservation_options: ReservableProductReservationOptionUpdateInput.array().parse(reservationOptions)
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    updateProductTracking: {
        mutationKey: ['reservations', 'updateProductTracking'],
        mutationFn: ({ id, isIndividuallyTracked, stockQuantity }) => API.httpClient.patch(`reservations/reservable-products/${id}/tracking`, {
            is_individually_tracked: isIndividuallyTracked,
            stock_quantity: stockQuantity
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    updateBookingAppSettings: {
        mutationKey: ['reservations', 'updateBookingAppSettings'],
        mutationFn: ({ id, maxAdvanceBookingMinutes, minAdvanceBookingMinutes, bookableWindowStart, bookableWindowEnd }) => API.httpClient.patch(`reservations/reservable-products/${id}/booking-apps`, {
            ...(maxAdvanceBookingMinutes !== undefined ? { max_advance_booking_minutes: maxAdvanceBookingMinutes } : {}),
            ...(minAdvanceBookingMinutes !== undefined ? { min_advance_booking_minutes: minAdvanceBookingMinutes } : {}),
            bookable_window_start: bookableWindowStart,
            bookable_window_end: bookableWindowEnd
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    updateSelfCheckinSettings: {
        mutationKey: ['reservations', 'updateSelfCheckinSettings'],
        mutationFn: ({ id, checkInMode, checkOutMode, applyToUpcoming, staticAccessCode, staticAccessCodeTitle, selfCheckinReminderOffsetMinutes, reservationProductInformation, lock }) => API.httpClient.patch(`reservations/reservable-products/${id}/self-checkin`, {
            check_in_mode: checkInMode,
            check_out_mode: checkOutMode,
            apply_to_upcoming: applyToUpcoming ?? false,
            static_access_code: staticAccessCode,
            static_access_code_title: staticAccessCodeTitle,
            self_checkin_reminder_offset_minutes: selfCheckinReminderOffsetMinutes,
            reservation_product_information: reservationProductInformation,
            lock
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    updateConfirmationEmailText: {
        mutationKey: ['reservations', 'updateConfirmationEmailText'],
        mutationFn: ({ id, title, body }) => API.httpClient.patch(`reservations/reservable-products/${id}/confirmation-email-text`, {
            title,
            body
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    /** Campingens schemalagda tillvalsmejl: om det skickas och när. */
    addonsEmailSettings: queryOptions({
        queryKey: ['reservations', 'addonsEmailSettings'],
        queryFn: () => API.httpClient.get('reservations/addons-email-settings')
            .then(API.assertSuccessResponse)
            .then(z.object({
            settings: AddonsEmailSettings
        }).transform(data => data.settings).parse)
    }),
    /** Tillvalsmejlet som gästen får det, för en påhittad bokning, som HTML. */
    addonsEmailPreview: (lang) => queryOptions({
        queryKey: ['reservations', 'addonsEmailPreview', lang],
        queryFn: () => API.httpClient.get('reservations/addons-email-settings/preview', {
            params: { lang },
            responseType: 'text',
            headers: { Accept: 'text/html' }
        })
            .then(response => {
            // Fel kommer som JSON trots att svaret läses som text
            if (String(response.headers['content-type'] ?? '').includes('application/json')) {
                API.assertSuccessResponse({ ...response, data: JSON.parse(response.data) });
            }
            return z.string().parse(response.data);
        })
    }),
    updateAddonsEmailSettings: {
        mutationKey: ['reservations', 'updateAddonsEmailSettings'],
        mutationFn: (settings) => API.httpClient.put('reservations/addons-email-settings', settings)
            .then(API.assertSuccessResponse)
            .then(z.object({
            settings: AddonsEmailSettings
        }).transform(data => data.settings).parse)
    },
    /** Alla produkter som försäljningskanalen kan sälja, med om den gör det, högsta antal och enheter. */
    salesChannelAssortment: (channel) => queryOptions({
        queryKey: ['reservations', 'salesChannelAssortment', channel],
        queryFn: () => API.httpClient.get(`reservations/sales-channels/${channel}/assortment`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            products: z.array(SalesChannelAssortmentProduct)
        }).transform(data => data.products).parse)
    }),
    /** Ersätter kanalens sortiment: produkter som inte skickas med säljs inte längre där. */
    updateSalesChannelAssortment: {
        mutationKey: ['reservations', 'updateSalesChannelAssortment'],
        mutationFn: ({ channel, offers }) => API.httpClient.put(`reservations/sales-channels/${channel}/assortment`, { products: offers })
            .then(API.assertSuccessResponse)
            .then(z.object({
            products: z.array(SalesChannelAssortmentProduct)
        }).transform(data => data.products).parse)
    },
    //
    // Pool Mode Stock Management (PATCH)
    //
    /**
     * Update stock quantity for pool mode products
     * Only works for products where is_individually_tracked = false
     * Returns mutation options for useMutation
     */
    updatePoolModeStockQuantity: {
        mutationKey: ['reservations', 'updatePoolModeStockQuantity'],
        mutationFn: ({ productId, stockQuantity }) => API.httpClient.patch(`reservations/reservable-products/${productId}/stock-quantity`, { stock_quantity: stockQuantity })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: UpdatePoolModeStockQuantitySuccessResponse
        }).transform(data => data.product).parse)
    },
    //
    // Item Lifecycle Management (POST)
    //
    /**
     * Checks an item in and reports whether the guest was emailed their codes or check-out link.
     * The server emails the guest unless the request says not to.
     */
    checkInItem: {
        mutationKey: ['reservations', 'checkInItem'],
        mutationFn: ({ itemId, deliveredAt = null }) => checkInItem(itemId, deliveredAt)
    },
    /** checkInItem for several items at once, in parallel. */
    checkInItems: {
        mutationKey: ['reservations', 'checkInItems'],
        mutationFn: ({ itemIds, deliveredAt = null }) => Promise.all(itemIds.map(itemId => checkInItem(itemId, deliveredAt)))
    },
    /**
     * Mark multiple items as returned in parallel (for grouped items).
     * All requests are fired simultaneously to avoid race conditions and UI flicker.
     * Returns mutation options for useMutation
     */
    markGroupAsReturned: {
        mutationKey: ['reservations', 'markGroupAsReturned'],
        mutationFn: ({ itemIds, returnedAt = null }) => Promise.all(itemIds.map(itemId => API.httpClient.post(`reservations/reservation-items/${itemId}/mark-returned`, {
            returned_at: returnedAt?.toISOString()
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            reservation_item: ReservationItemResource
        }).transform(data => data.reservation_item).parse)))
    },
    /**
     * Checkar ut de försenade bland reservationerna vid deras planerade sluttid, utan mejl till gästerna.
     * Andra reservationer hoppas över. Ger hur många som checkades ut.
     */
    bulkCheckOutOverdue: {
        mutationKey: ['reservations', 'bulkCheckOutOverdue'],
        mutationFn: ({ itemIds }) => API.httpClient.post('reservations/reservation-items/bulk-check-out', {
            reservation_item_ids: itemIds
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            checked_out_count: z.number()
        }).transform(data => data.checked_out_count).parse)
    },
    /**
     * Registrerar alla uteblivna reservationer som matchar filtren som incheckade vid planerad start och
     * utcheckade vid planerat slut, utan koder och mejl. Ger hur många som registrerades.
     */
    bulkCompleteNoShows: {
        mutationKey: ['reservations', 'bulkCompleteNoShows'],
        mutationFn: async ({ filters }) => {
            const pageSize = 500;
            const itemIds = [];
            for (let offset = 0;; offset += pageSize) {
                const page = await fetchReservationItems({ ...filters, statuses: ['no_show'], offset, limit: pageSize });
                itemIds.push(...page.reservation_items.map(item => item.id));
                if (page.reservation_items.length < pageSize) {
                    break;
                }
            }
            let completedCount = 0;
            for (let start = 0; start < itemIds.length; start += 1000) {
                completedCount += await API.httpClient.post('reservations/reservation-items/bulk-complete-no-shows', {
                    reservation_item_ids: itemIds.slice(start, start + 1000)
                })
                    .then(API.assertSuccessResponse)
                    .then(z.object({
                    completed_count: z.number()
                }).transform(data => data.completed_count).parse);
            }
            return completedCount;
        }
    },
    /**
     * Mark item as returned when customer brings it back
     * Returns mutation options for useMutation
     */
    markItemAsReturned: {
        mutationKey: ['reservations', 'markItemAsReturned'],
        mutationFn: ({ itemId, returnedAt }) => API.httpClient.post(`reservations/reservation-items/${itemId}/mark-returned`, {
            returned_at: returnedAt?.toISOString()
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            reservation_item: ReservationItemResource
        }).transform(data => data.reservation_item).parse)
    },
    /**
     * Undo a delivery marking (administrative correction)
     * Cannot reverse if item already returned
     * Returns mutation options for useMutation
     */
    reverseDelivery: {
        mutationKey: ['reservations', 'reverseDelivery'],
        mutationFn: ({ itemId, reason }) => API.httpClient.post(`reservations/reservation-items/${itemId}/reverse-delivery`, {
            reason
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            reservation_item: ReservationItemResource
        }).transform(data => data.reservation_item).parse)
    },
    /**
     * Undo a return marking (for re-rentals or corrections)
     * Returns mutation options for useMutation
     */
    reverseReturn: {
        mutationKey: ['reservations', 'reverseReturn'],
        mutationFn: ({ itemId, reason }) => API.httpClient.post(`reservations/reservation-items/${itemId}/reverse-return`, { reason })
            .then(API.assertSuccessResponse)
            .then(z.object({
            reservation_item: ReservationItemResource
        }).transform(data => data.reservation_item).parse)
    },
    //
    // Customer Emails
    // The server responds with a snackbar for both success and failure.
    //
    getReservationEmails: (reservationId) => queryOptions({
        queryKey: ['reservations', 'reservationEmails', reservationId],
        queryFn: () => API.httpClient.get(`reservations/reservations/${reservationId}/emails`)
            .then(API.assertSuccessResponse)
            .then(z.object({ emails: z.array(ReservationSentEmail) }).parse)
            .then(data => data.emails)
    }),
    /**
     * Send the booking confirmation for the whole reservation.
     * Never checks anything in, unlike the confirmation sent after payment.
     */
    sendReservationConfirmation: {
        mutationKey: ['reservations', 'sendReservationConfirmation'],
        mutationFn: ({ reservationId }) => API.httpClient.post(`reservations/reservations/${reservationId}/send-confirmation`)
            .then(API.assertSuccessResponse)
    },
    /**
     * Send the check-in confirmation with access codes for a checked-in item.
     */
    sendItemCheckinConfirmation: {
        mutationKey: ['reservations', 'sendItemCheckinConfirmation'],
        mutationFn: ({ itemId }) => API.httpClient.post(`reservations/reservation-items/${itemId}/send-checkin-confirmation`)
            .then(API.assertSuccessResponse)
    },
    /**
     * Send the pickup reminder. Only allowed for self check-in items starting tomorrow,
     * since the email tells the guest the reservation starts tomorrow.
     */
    sendItemPickupReminder: {
        mutationKey: ['reservations', 'sendItemPickupReminder'],
        mutationFn: ({ itemId }) => API.httpClient.post(`reservations/reservation-items/${itemId}/send-pickup-reminder`)
            .then(API.assertSuccessResponse)
    },
    /** Reminds a checked-in guest who checks out themselves; the scheduled reminder is then not sent */
    sendItemCheckoutReminder: {
        mutationKey: ['reservations', 'sendItemCheckoutReminder'],
        mutationFn: ({ itemId }) => API.httpClient.post(`reservations/reservation-items/${itemId}/send-checkout-reminder`)
            .then(API.assertSuccessResponse)
    },
    /**
     * Send the addons email to a booking's guest now, whatever the schedule.
     * The outcome says whether it was sent or why not.
     */
    sendBookingAddonsEmail: {
        mutationKey: ['reservations', 'sendBookingAddonsEmail'],
        mutationFn: ({ bookingId }) => API.httpClient.post('bookings/send-addons-email', { booking_id: bookingId })
            .then(API.assertSuccessResponse)
            .then(SendBookingAddonsEmailResult.parse)
    },
    //
    // Reservable Product Items Management (DELETE)
    //
    /**
     * Cancel a reservation item
     * Cannot cancel items that are delivered but not yet returned
     * Returns mutation options for useMutation
     */
    cancelItem: {
        mutationKey: ['reservations', 'cancelItem'],
        mutationFn: ({ itemId, reason = null, refund = false }) => API.httpClient.delete(`reservations/reservation-items/${itemId}`, {
            data: {
                reason,
                refund
            }
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            id: EntityId,
            cancelled_at: isoDateTimeToDayJs,
            refund_processed: z.boolean()
        }).parse)
    },
    /**
     * Delete a reservable product category (soft delete).
     * Blocked if the category has non-deleted products.
     */
    deleteReservableProductCategory: {
        mutationKey: ['reservations', 'deleteReservableProductCategory'],
        mutationFn: (categoryId) => API.httpClient.delete(`reservations/categories/${categoryId}`)
            .then(API.assertSuccessResponse)
    },
    /**
     * Create a new reservable product category
     */
    createReservableProductCategory: {
        mutationKey: ['reservations', 'createReservableProductCategory'],
        mutationFn: (data) => API.httpClient.post('reservations/categories', data)
            .then(API.assertSuccessResponse)
            .then(ReservableProductCategoryResource.parse)
    },
    updateReservableProductCategoryName: {
        mutationKey: ['reservations', 'updateReservableProductCategoryName'],
        mutationFn: ({ categoryId, title }) => API.httpClient.patch(`reservations/categories/${categoryId}/name`, { title })
            .then(API.assertSuccessResponse)
    },
    /**
     * Create a new reservable product (admin)
     */
    createReservableProduct: {
        mutationKey: ['reservations', 'createReservableProduct'],
        mutationFn: (data) => API.httpClient.post('reservations/reservable-products', data)
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    /**
     * Update bookable_from / bookable_until for a product
     */
    updateReservableProductDates: {
        mutationKey: ['reservations', 'updateReservableProductDates'],
        mutationFn: ({ id, bookableFrom, bookableUntil }) => API.httpClient.patch(`reservations/reservable-products/${id}/dates`, {
            bookable_from: bookableFrom,
            bookable_until: bookableUntil
        })
            .then(API.assertSuccessResponse)
    },
    updateReservableProductWeekdays: {
        mutationKey: ['reservations', 'updateReservableProductWeekdays'],
        mutationFn: ({ id, bookableWeekdays }) => API.httpClient.patch(`reservations/reservable-products/${id}/weekdays`, {
            bookable_weekdays: bookableWeekdays
        })
            .then(API.assertSuccessResponse)
    },
    /**
     * Get a presigned URL for uploading a product thumbnail to R2
     */
    getProductThumbnailPresignedUrl: {
        mutationKey: ['reservations', 'getProductThumbnailPresignedUrl'],
        mutationFn: (input) => API.httpClient.post(`reservations/reservable-products/${input.id}/thumbnail-presigned-url`, {
            filename: input.filename,
            content_type: input.contentType
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            thumbnail_upload: z.object({
                presigned_url: z.string(),
                headers: z.record(z.string(), z.array(z.string())).optional(),
                public_url: z.string()
            })
        }).transform(data => data.thumbnail_upload).parse)
            .then(data => ({
            presignedUrl: data.presigned_url,
            headers: data.headers ?? {},
            publicUrl: data.public_url
        }))
    },
    /**
     * Save a public R2 URL as the product thumbnail
     */
    updateProductThumbnail: {
        mutationKey: ['reservations', 'updateProductThumbnail'],
        mutationFn: ({ id, thumbnail }) => API.httpClient.patch(`reservations/reservable-products/${id}/thumbnail`, { thumbnail })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    /**
     * Clear the product thumbnail
     */
    clearProductThumbnail: {
        mutationKey: ['reservations', 'clearProductThumbnail'],
        mutationFn: ({ id }) => API.httpClient.delete(`reservations/reservable-products/${id}/thumbnail`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    updateProductCategory: {
        mutationKey: ['reservations', 'updateProductCategory'],
        mutationFn: ({ id, reservableProductCategoryId }) => API.httpClient.patch(`reservations/reservable-products/${id}/category`, {
            reservable_product_category_id: reservableProductCategoryId
        })
            .then(API.assertSuccessResponse)
            .then(z.object({
            product: ReservableProductResource
        }).transform(data => data.product).parse)
    },
    //
    // Stock Management (DELETE)
    //
    /**
     * Delete a reservable product (soft delete).
     * Blocked if there are upcoming reservations.
     * Cascades deletion to all child reservables.
     */
    deleteReservableProduct: {
        mutationKey: ['reservations', 'deleteReservableProduct'],
        mutationFn: (productId) => API.httpClient.delete(`reservations/reservable-products/${productId}`)
            .then(API.assertSuccessResponse)
    },
    /**
     * Soft delete a reservable (stock item)
     * Cannot delete items currently in use
     * Returns mutation options for useMutation
     */
    deleteReservable: {
        mutationKey: ['reservations', 'deleteReservable'],
        mutationFn: (reservableId) => API.httpClient.delete(`reservations/reservables/${reservableId}`)
            .then(API.assertSuccessResponse)
            .then(z.object({
            id: EntityId,
            deleted_at: isoDateTimeToDayJs,
            upcoming_bookings_count: z.int(),
            warning: z.string().nullable()
        }).parse)
    },
    reorderCategories: {
        mutationKey: ['reservations', 'reorderCategories'],
        mutationFn: (categoryIds) => API.httpClient.post('reservations/categories/reorder', { category_ids: categoryIds })
            .then(API.assertSuccessResponse)
    },
    reorderProducts: {
        mutationKey: ['reservations', 'reorderProducts'],
        mutationFn: (productIds) => API.httpClient.post('reservations/reservable-products/reorder', { product_ids: productIds })
            .then(API.assertSuccessResponse)
    }
};
